import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ordersAPI } from '../../lib/api';
import { fetchWithCache, clearAllCaches } from '../../lib/cache';
import { toast } from 'sonner';
import { usePopup } from '../../contexts/PopupContext';
import { Search, FileText, Package, RefreshCcw, ShoppingCart, Printer, Trash2, Receipt } from 'lucide-react';
import { printInvoice as generatePrintInvoice } from '../../utils/printInvoice';

export default function AdminQuotations() {
    useSearchParams();
    const navigate = useNavigate();
    const { showPopup } = usePopup();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('pending');
    const [showDialog, setShowDialog] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [printSize, setPrintSize] = useState('a4'); // 'a4' or 'thermal'
    const [showPrintDialog, setShowPrintDialog] = useState(false);
    const [printOrder, setPrintOrder] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            // We fetch all orders and filter client-side for now to handle complex logic
            // Ideally backend supports this, but we're adapting swiftly
            await fetchWithCache(
                'admin_quotations_data',
                async () => {
                    const res = await ordersAPI.getAll();
                    return res.data.orders || [];
                },
                (data, isFromCache) => {
                    setOrders(data);
                    if (isFromCache) setLoading(false);
                }
            );
        } catch (error) {
            console.error('Failed to fetch quotations:', error);
        } finally {
            setLoading(false);
        }
    };

    const getFilteredOrders = () => {
        let filtered = orders.filter(o => o.is_offline); // Base filter: must be offline/POS related

        if (filter === 'pending') {
            filtered = filtered.filter(o => o.status === 'quotation');
        } else if (filter === 'sold') {
            filtered = filtered.filter(o => o.status === 'completed' || o.status === 'delivered');
        } else if (filter === 'closed') {
            filtered = filtered.filter(o => o.status === 'cancelled');
        }

        if (search) {
            const q = search.toLowerCase();
            filtered = filtered.filter(o =>
                (o.order_number || '').toLowerCase().includes(q) ||
                (o.customer_phone || '').includes(q) ||
                (o.customer_name || '').toLowerCase().includes(q)
            );
        }

        return filtered;
    };

    const filteredOrders = getFilteredOrders();

    const handleViewOrder = (order) => {
        setSelectedOrder(order);
        setShowDialog(true);
    };

    const handlePrintInvoice = (order) => {
        // Open print size chooser
        if (typeof order === 'string') {
            // Called with just orderId — find order from list
            const found = orders.find(o => o.id === order);
            if (found) { setPrintOrder(found); setShowPrintDialog(true); }
            return;
        }
        setPrintOrder(order);
        setShowPrintDialog(true);
    };

    const executePrint = () => {
        if (!printOrder) return;
        generatePrintInvoice(printOrder, printSize);
        setShowPrintDialog(false);
    };

    const handleDeleteQuotation = (order) => {
        showPopup({
            title: "Delete Quotation",
            message: `Are you sure you want to delete quotation ${order.order_number}? This action cannot be undone.`,
            type: "error",
            onConfirm: async () => {
                try {
                    await ordersAPI.delete(order.id);
                    toast.success('Quotation deleted successfully');
                    clearAllCaches();
                    fetchData();
                } catch (error) {
                    console.error('Delete quotation error:', error);
                    const errorMessage = error.response?.data?.detail
                        || error.message
                        || 'Failed to delete quotation';
                    toast.error(errorMessage);
                }
            }
        });
    };

    return (
        <div className="space-y-6" data-testid="admin-quotations">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Quotations</h1>
                    <p className="text-slate-400">Manage offline quotations and sales</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={async () => {
                            setRefreshing(true);
                            await fetchData();
                            setRefreshing(false);
                        }}
                        className="border-slate-600 hover:bg-slate-700"
                    >
                        <RefreshCcw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                    <Button
                        onClick={() => navigate('/admin/pos')}
                        className="bg-primary hover:bg-primary/90"
                    >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        New Quotation (POS)
                    </Button>
                </div>
            </div>

            <div className="flex flex-wrap gap-4">
                <Tabs value={filter} onValueChange={setFilter}>
                    <TabsList className="bg-slate-800">
                        <TabsTrigger value="pending">Pending</TabsTrigger>
                        <TabsTrigger value="sold">Sold</TabsTrigger>
                        <TabsTrigger value="closed">Closed</TabsTrigger>
                    </TabsList>
                </Tabs>
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                        placeholder="Search by ID, name or phone..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 input-admin"
                    />
                </div>
            </div>

            <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-slate-700 hover:bg-transparent">
                                <TableHead className="text-slate-400">Date</TableHead>
                                <TableHead className="text-slate-400">ID</TableHead>
                                <TableHead className="text-slate-400">Customer</TableHead>
                                <TableHead className="text-slate-400">Items</TableHead>
                                <TableHead className="text-slate-400">Amount</TableHead>
                                <TableHead className="text-slate-400">Status</TableHead>
                                <TableHead className="text-slate-400 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
                                </TableRow>
                            ) : filteredOrders.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                                        <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                        No quotations found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredOrders.map((order) => (
                                    <TableRow key={order.id} className="border-slate-700">
                                        <TableCell className="text-slate-400">
                                            {new Date(order.created_at).toLocaleDateString('en-IN')}
                                        </TableCell>
                                        <TableCell className="font-mono font-medium">{order.order_number}</TableCell>
                                        <TableCell>
                                            <div>
                                                <p className="font-medium">{order.customer_name || 'Walk-in'}</p>
                                                <p className="text-xs text-slate-400">{order.customer_phone}</p>
                                            </div>
                                        </TableCell>
                                        <TableCell>{order.items.length} items</TableCell>
                                        <TableCell className="font-bold">₹{order.grand_total.toLocaleString()}</TableCell>
                                        <TableCell>
                                            <Badge className={
                                                order.status === 'quotation' ? 'bg-amber-500/20 text-amber-400' :
                                                    order.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                                                        'bg-red-500/20 text-red-400'
                                            }>
                                                {order.status === 'quotation' ? 'Pending' : order.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button size="sm" variant="outline" className="h-8 border-slate-600" onClick={() => handleViewOrder(order)}>
                                                    View
                                                </Button>
                                                <Button size="sm" variant="outline" className="h-8 border-slate-600" onClick={() => handlePrintInvoice(order)}>
                                                    Invoice
                                                </Button>
                                                {order.status === 'quotation' && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-8 border-red-600 text-red-400 hover:bg-red-500/10"
                                                        onClick={() => handleDeleteQuotation(order)}
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="max-w-2xl bg-slate-800 border-slate-700">
                    <DialogHeader>
                        <DialogTitle>Quotation Details - {selectedOrder?.order_number}</DialogTitle>
                    </DialogHeader>
                    {selectedOrder && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4 text-sm bg-slate-700/50 p-3 rounded-lg">
                                <div>
                                    <p className="text-slate-400">Customer</p>
                                    <p className="font-medium">{selectedOrder.customer_name}</p>
                                    <p>{selectedOrder.customer_phone}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-slate-400">Date</p>
                                    <p>{new Date(selectedOrder.created_at).toLocaleString()}</p>
                                </div>
                            </div>

                            <div>
                                <h4 className="font-semibold mb-2">Items</h4>
                                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                                    {selectedOrder.items?.map((item, index) => (
                                        <div key={index} className="flex justify-between p-2 bg-slate-700/30 rounded">
                                            <div>
                                                <p className="font-medium text-sm">{item.product_name}</p>
                                                <p className="text-xs text-slate-400">Qty: {item.quantity} x ₹{item.price}</p>
                                            </div>
                                            <div className="font-mono">₹{item.total}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex justify-between items-center pt-4 border-t border-slate-700">
                                <span className="text-lg font-bold">Total Amount</span>
                                <span className="text-xl font-bold text-primary">₹{selectedOrder.grand_total.toLocaleString()}</span>
                            </div>

                            <div className="flex justify-end gap-3 pt-2">
                                <Button onClick={() => handlePrintInvoice(selectedOrder)} variant="outline">
                                    <Printer className="w-4 h-4 mr-2" />
                                    Print Invoice
                                </Button>
                                {selectedOrder.status === 'quotation' && (
                                    <Button
                                        onClick={() => {
                                            setShowDialog(false);
                                            handleDeleteQuotation(selectedOrder);
                                        }}
                                        variant="outline"
                                        className="border-red-600 text-red-400 hover:bg-red-500/10"
                                    >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Delete
                                    </Button>
                                )}
                                <Button onClick={() => setShowDialog(false)}>Close</Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Print Size Dialog */}
            <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
                <DialogContent className="bg-slate-800 border-slate-700 max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Printer className="w-5 h-5" />
                            Choose Print Size
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <Label className="text-sm text-slate-300">Select print format</Label>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={() => setPrintSize('a4')}
                                className={`p-4 rounded-lg border-2 text-left transition-colors ${printSize === 'a4' ? 'border-primary bg-primary/10' : 'border-slate-600 hover:border-slate-500'}`}
                            >
                                <FileText className="w-6 h-6 mb-2 text-primary" />
                                <p className="font-medium">A4 Size</p>
                                <p className="text-xs text-slate-400 mt-1">Full invoice with product images</p>
                            </button>
                            <button
                                onClick={() => setPrintSize('thermal')}
                                className={`p-4 rounded-lg border-2 text-left transition-colors ${printSize === 'thermal' ? 'border-primary bg-primary/10' : 'border-slate-600 hover:border-slate-500'}`}
                            >
                                <Receipt className="w-6 h-6 mb-2 text-primary" />
                                <p className="font-medium">Thermal</p>
                                <p className="text-xs text-slate-400 mt-1">Compact 58mm/80mm format</p>
                            </button>
                        </div>
                        <div className="flex gap-2 pt-2">
                            <Button className="flex-1" onClick={executePrint}>
                                <Printer className="w-4 h-4 mr-2" />
                                Print Now
                            </Button>
                            <Button variant="outline" onClick={() => setShowPrintDialog(false)}>Cancel</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
